import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Button from 'components/_UI/Button';
import Loader from 'components/_UI/Loader';
import Text from 'components/_UI/Text';
import { ArrowLeft, Crosshair, PlusSquare } from 'react-feather';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import breakpoints from 'theme/breakpoints';
import PixelCard from '../pixels/PixelCard';
import PixelForm from '../pixels/PixelForm';
import PixelDeleteModal from '../pixels/PixelDeleteModal';

export default function PixelsSection({ data, loading, onBack, toast, onSuccess }) {
    const [modalPixel, setModalPixel] = useState(false);
    const [deletePixelModal, setDeletePixelModal] = useState(false);

    function handleSuccess(item) {
        if (item === 'createPixel') {
            setModalPixel(false);
            toast.success('Pixel criado com sucesso!', { position: 'top-center' });
        }
        if (item === 'editPixel') {
            setModalPixel(false);
            toast.success('Pixel atualizado com sucesso!', { position: 'top-center' });
        }
        if (item === 'deletePixel') {
            setDeletePixelModal(false);
            toast.success('Pixel excluído com sucesso!', { position: 'top-center' });
        }
        onSuccess();
    }

    const count = data?.length || 0;

    return (
        <Wrapper>
            <BackRow
                onClick={onBack}
                role="button"
                tabIndex={0}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onBack()}
            >
                <BackIconWrap>
                    <ArrowLeft size={18} color={colors.secondaryPurple} />
                </BackIconWrap>
                <BackLabel>Voltar</BackLabel>
            </BackRow>

            <SectionCard>
                <SectionHeader>
                    <HeaderLeft>
                        <IconBadge>
                            <Crosshair size={20} color={colors.occasionalPurple} />
                        </IconBadge>
                        <HeaderText>
                            <TitleRow>
                                <SectionTitle>Pixels de rastreamento</SectionTitle>
                                {count > 0 && <CountBadge>{count}</CountBadge>}
                            </TitleRow>
                            <SectionDescription>
                                Adicione pixels de rastreamento para monitorar conversões e otimizar campanhas.
                            </SectionDescription>
                        </HeaderText>
                    </HeaderLeft>

                    <ActionsRow>
                        <Button
                            variant='featured'
                            category='squired'
                            onClick={() => setModalPixel(true)}
                        >
                            <PlusSquare color={colors.neutralLight} size={16} />
                            <Text
                                color={colors.neutralLight}
                                paddingBottom={0}
                                marginLeft={'6px'}
                            >
                                Adicionar pixel
                            </Text>
                        </Button>
                    </ActionsRow>
                </SectionHeader>

                <Divider />

                {loading ? (
                    <LoaderWrap>
                        <Loader />
                    </LoaderWrap>
                ) : count > 0 ? (
                    <PixelsGrid>
                        {data.map(pixel => (
                            <PixelCard
                                key={pixel.id}
                                data={pixel}
                                onEdit={() => setModalPixel({ type: 'editPixel', item: pixel })}
                                onDelete={() => setDeletePixelModal({ type: 'deletePixel', item: pixel })}
                            />
                        ))}
                    </PixelsGrid>
                ) : (
                    <EmptyState>
                        <EmptyIcon>
                            <Crosshair size={28} color={colors.nightLight} />
                        </EmptyIcon>
                        <Text color={colors.night} textAlign='center' paddingBottom={0} name='small'>
                            Nenhum pixel de rastreamento adicionado ainda.
                        </Text>
                        <EmptyHint>
                            Conecte suas plataformas de anúncios para medir resultados.
                        </EmptyHint>
                    </EmptyState>
                )}
            </SectionCard>

            {modalPixel && (
                <PixelForm
                    visible={modalPixel}
                    setVisible={setModalPixel}
                    edit={modalPixel?.item || false}
                    onSuccess={handleSuccess}
                />
            )}

            {deletePixelModal?.type === 'deletePixel' && (
                <PixelDeleteModal
                    visible={!!deletePixelModal}
                    data={deletePixelModal?.item}
                    onClose={() => setDeletePixelModal(false)}
                    onSuccess={handleSuccess}
                />
            )}
        </Wrapper>
    );
}

const slideIn = keyframes`
    from { opacity: 0; transform: translateX(16px); }
    to { opacity: 1; transform: translateX(0); }
`;

const Wrapper = styled.div`
    animation: ${slideIn} 0.25s ease-out;
`;

const BackRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 6px 12px 6px 6px;
    margin-bottom: 16px;
    width: fit-content;
    border-radius: 10px;
    outline: none;
    transition: background 0.15s ease;

    &:hover { background: ${colors.neutralDark}; }
    &:focus-visible { box-shadow: 0 0 0 2px ${colors.secondaryPurple}44; }
`;

const BackIconWrap = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${colors.occasionalPurple}10;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const BackLabel = styled.span`
    font-size: ${fonts.sizes.default};
    font-weight: 500;
    color: ${colors.nightDark};
`;

const SectionCard = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 24px;
    border: 1px solid ${colors.night}11;
    box-shadow: 0 1px 8px ${colors.night}08;
`;

const SectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 14px;
`;

const IconBadge = styled.div`
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 10px;
    background: ${colors.occasionalPurple}12;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SectionTitle = styled.span`
    font-size: ${fonts.sizes.highlight};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const CountBadge = styled.span`
    font-size: ${fonts.sizes.mini};
    font-weight: 700;
    color: ${colors.neutralLight};
    background: ${colors.secondaryPurple};
    padding: 2px 8px;
    border-radius: 20px;
    line-height: 1.4;
`;

const SectionDescription = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    line-height: 1.5;
`;

const ActionsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const Divider = styled.div`
    height: 1px;
    background: ${colors.night}11;
    margin: 20px 0;
`;

const LoaderWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
`;

const PixelsGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
    border: 2px dashed ${colors.night}22;
    border-radius: 12px;
    background: ${colors.neutralDark}88;
`;

const EmptyIcon = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: ${colors.night}0A;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
`;

const EmptyHint = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
    margin-top: 6px;
    text-align: center;
`;
